import { useState } from 'react';
import type {
  SiteContact,
  ConflictExistingMember,
  MemberConflictMatchedBy,
} from '@safetyhub/shared';
import { createSiteContact } from '../lib/tbm';
import { extractApiError, getHttpStatus } from '../lib/api';
import type { PendingContactInput } from '../components/sites/MemberPhoneConflictModal';

interface ConflictState {
  existingMember: ConflictExistingMember;
  pendingInput: PendingContactInput;
  /** 무엇이 겹쳤는지 — 안내 문구와 "다른 사람" 선택지 노출을 가른다 */
  matchedBy: MemberConflictMatchedBy;
}

interface UseSiteContactCreatorResult {
  /** 인원 중복 상태. null이면 모달 표시 불필요 */
  conflict: ConflictState | null;
  isCreating: boolean;
  /** SiteContact 생성 요청 (409 충돌 시 conflict state로 전환) */
  create: (
    companyId: string,
    siteId: string,
    input: PendingContactInput,
  ) => Promise<SiteContact | null>;
  /** 충돌 모달에서 "이 인물로 등록" 선택 시 호출 */
  selectExisting: (
    companyId: string,
    siteId: string,
    memberId: string,
  ) => Promise<SiteContact | null>;
  /** 이메일만 겹친 경우 "다른 사람입니다" 선택 시 호출 — 그대로 새로 등록 */
  createAsDifferentPerson: (
    companyId: string,
    siteId: string,
  ) => Promise<SiteContact | null>;
  /** 충돌 모달에서 "다시 입력" 선택 시 호출 */
  cancelConflict: () => void;
}

/**
 * SiteContact 생성 흐름을 담당하는 hook.
 * 인원 중복(409 MEMBER_PHONE_CONFLICT) 발생 시 conflict state로 전환하여
 * 호출처가 MemberPhoneConflictModal을 열도록 한다.
 * 코드 이름은 PHONE이지만 전화번호·이메일 중복을 함께 나른다 — `matchedBy`로 구분한다.
 */
export function useSiteContactCreator(): UseSiteContactCreatorResult {
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  async function create(
    companyId: string,
    siteId: string,
    input: PendingContactInput,
  ): Promise<SiteContact | null> {
    setIsCreating(true);
    try {
      const created = await createSiteContact(companyId, siteId, {
        name: input.name,
        phone: input.phone,
        department: input.department,
        position: input.position,
        email: input.email,
      });
      setConflict(null);
      return created;
    } catch (err) {
      const status = getHttpStatus(err);
      if (status === 409) {
        const apiError = extractApiError(err);
        if (
          apiError.code === 'MEMBER_PHONE_CONFLICT' &&
          apiError.details &&
          'existingMember' in apiError.details
        ) {
          const details = apiError.details as {
            existingMember: ConflictExistingMember;
            matchedBy?: MemberConflictMatchedBy;
          };
          setConflict({
            existingMember: details.existingMember,
            pendingInput: input,
            // 구 서버 응답에는 matchedBy가 없다 — 그때는 기존 안내(전화번호)를 유지한다.
            matchedBy: details.matchedBy ?? 'phone',
          });
          return null;
        }
      }
      throw err;
    } finally {
      setIsCreating(false);
    }
  }

  async function selectExisting(
    companyId: string,
    siteId: string,
    memberId: string,
  ): Promise<SiteContact | null> {
    if (!conflict) return null;
    setIsCreating(true);
    try {
      const created = await createSiteContact(companyId, siteId, {
        name: conflict.pendingInput.name,
        phone: conflict.pendingInput.phone,
        department: conflict.pendingInput.department,
        position: conflict.pendingInput.position,
        memberId,
      });
      setConflict(null);
      return created;
    } finally {
      setIsCreating(false);
    }
  }

  async function createAsDifferentPerson(
    companyId: string,
    siteId: string,
  ): Promise<SiteContact | null> {
    if (!conflict) return null;
    setIsCreating(true);
    try {
      const created = await createSiteContact(companyId, siteId, {
        name: conflict.pendingInput.name,
        phone: conflict.pendingInput.phone,
        department: conflict.pendingInput.department,
        position: conflict.pendingInput.position,
        email: conflict.pendingInput.email,
        allowDuplicateEmail: true,
      });
      setConflict(null);
      return created;
    } finally {
      setIsCreating(false);
    }
  }

  function cancelConflict() {
    setConflict(null);
  }

  return {
    conflict,
    isCreating,
    create,
    selectExisting,
    createAsDifferentPerson,
    cancelConflict,
  };
}
